import React, { useMemo } from 'react';
import { InputNumber, Select, Tooltip } from 'antd';
import { BoldOutlined, ItalicOutlined, UnderlineOutlined } from '@ant-design/icons';
import { useParams } from 'react-router-dom';
import { debounce } from 'lodash';
import Flyout from './Flyout';
import shared from './Flyout.module.css';
import styles from './TextFlyout.module.css';
import { useCanvas } from '../../../context/canvasContext';
import { useDocument } from '../../../context/documentInfoContex';
import { boardServices } from '../../../api';

const FONTS = ['Arial', 'Times New Roman', 'Comic Sans MS', 'Tahoma', 'Verdana'];
const STYLES = [
    ['bold', 'Bold', BoldOutlined],
    ['italic', 'Italic', ItalicOutlined],
    ['underline', 'Underline', UnderlineOutlined],
];

/** Colour, size, font and style of the selected text object. */
const TextFlyout = ({ text, onClose }) => {
    const { id } = useParams();
    const { canvasRef } = useCanvas();
    const { setSavingData, isTemplateDocument } = useDocument();

    const activeText = () => {
        const active = canvasRef.current?.getActiveObject();
        return active && active.isType('i-text') ? active : null;
    };

    const save = () => {
        const data = canvasRef.current.toJSON(['selectable', 'id', 'type']);
        boardServices.updateBoardData(id, data, setSavingData, isTemplateDocument);
    };

    // Colour and size change continuously; the board is saved once they settle.
    const saveDebounced = useMemo(() => debounce(save, 300), [id, setSavingData, isTemplateDocument]);

    const applyProps = (props, settle) => {
        const active = activeText();
        if (!active) return;
        active.set(props);
        canvasRef.current.renderAll();
        settle ? saveDebounced() : save();
    };

    const setColor = (color) => {
        text.setTextColor(color);
        applyProps({ fill: color }, true);
    };
    const setSize = (size) => {
        text.setTextSize(size);
        applyProps({ fontSize: size }, true);
    };
    const setFont = (font) => {
        text.setTextFont(font);
        applyProps({ fontFamily: font });
    };
    const toggleStyle = (style) => {
        const styles = text.textStyle?.includes(style)
            ? text.textStyle.filter((s) => s !== style)
            : [...(text.textStyle || []), style];
        text.setTextStyle(styles);
        applyProps({
            fontWeight: styles.includes('bold') ? 'bold' : 'normal',
            fontStyle: styles.includes('italic') ? 'italic' : 'normal',
            underline: styles.includes('underline'),
        });
    };

    return (
        <Flyout title="Text" onClose={onClose}>
            <div className={styles.row}>
                <span className={shared.label}>Colour</span>
                <Tooltip title="Text colour" placement="right">
                    <input
                        type="color"
                        className={shared.swatch}
                        value={text.textColor || '#000000'}
                        aria-label="Text colour"
                        onChange={(e) => setColor(e.target.value)}
                    />
                </Tooltip>
            </div>

            <span className={shared.label}>Size</span>
            <InputNumber size="small" min={1} value={text.textSize || 10} onChange={setSize} className={styles.size} aria-label="Text size" />

            <span className={shared.label}>Font</span>
            <Select size="small" value={text.textFont} onChange={setFont} className={styles.font} aria-label="Text font">
                {FONTS.map((font) => <Select.Option key={font} value={font}>{font}</Select.Option>)}
            </Select>

            <span className={shared.label}>Style</span>
            <div className={shared.segments}>
                {STYLES.map(([key, name, StyleIcon]) => {
                    const pressed = !!text.textStyle?.includes(key);
                    return (
                        <Tooltip key={key} title={name}>
                            <button
                                type="button"
                                className={`${shared.segment} ${pressed ? shared.segmentActive : ''}`}
                                aria-label={name}
                                aria-pressed={pressed}
                                onClick={() => toggleStyle(key)}
                            >
                                <StyleIcon />
                            </button>
                        </Tooltip>
                    );
                })}
            </div>
        </Flyout>
    );
};

export default TextFlyout;
